// Inbox, Reiter Messages (Runde 6, 6D, I-08, S08): eingegangene und gesendete SMS in einer Liste. Filter Offen, Ungelesen,
// Fehlgeschlagen, Geplant und Alle, Suche, Auswahlmodus mit Erledigt und Gelesen, Antworten aus der Zeile heraus. Ein Klick klappt die
// SMS mit ihrem Verlauf auf und markiert sie als gelesen. Tasten: j/k, Enter (auf/zu), r (antworten), x (markieren), e oder y
// (erledigt), u (ungelesen), ⌘ Enter (Lead öffnen), Esc. Neue SMS und Statuswechsel kommen per Echtzeit.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useEchtzeit } from "../../api/echtzeit";
import { smsEingangApi, type SmsZaehler, type SmsZeile as Sms } from "../../api/smsEingang";
import { useAuth } from "../../auth/AuthContext";
import { listeSetzen } from "../../daten/liste";
import { useNeuVerbunden } from "../../daten/verbindung";
import { useTasten } from "../../werkzeuge/tasten";
import { Laden, Leer } from "../Bausteine";
import { FehlerBox } from "../Fehler";
import { Icon } from "../Icon";
import { useMeldung } from "../Meldungen";
import { filterParameter, istUngelesen, passtZumFilter, SMS_FILTER, type SmsFilter } from "./nachrichtenLogik";
import { SmsVerlauf } from "./SmsVerlauf";
import { SmsZeile } from "./SmsZeile";
import "./nachrichten.css";

interface Props {
  /** me | all | user_… wie bei den Aufgaben */
  wer: string;
  zaehler: SmsZaehler | null;
  /** Zähler im Reiterkopf neu holen */
  onZaehler: () => void;
}

const LEER: Record<SmsFilter, { titel: string; text: string }> = {
  offen: { titel: "Alles erledigt.", text: "Keine offenen SMS und keine fehlgeschlagenen Sendungen." },
  ungelesen: { titel: "Nichts Ungelesenes.", text: "Alle eingegangenen SMS sind gelesen." },
  fehlgeschlagen: { titel: "Keine Fehler.", text: "Alle gesendeten SMS sind angekommen." },
  geplant: { titel: "Nichts geplant.", text: "Eine SMS lässt sich im Komposer über „Später senden“ planen." },
  alle: { titel: "Noch keine SMS.", text: "Eingegangene und gesendete SMS erscheinen hier." },
};

export function SmsReiter({ wer, zaehler, onZaehler }: Props) {
  const navigate = useNavigate();
  const meldung = useMeldung();
  const { nutzer } = useAuth();
  const [params, setParams] = useSearchParams();
  const filter = (SMS_FILTER.some((f) => f.wert === params.get("filter")) ? params.get("filter") : "offen") as SmsFilter;
  const [liste, setListe] = useState<Sms[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState<unknown>(null);
  const [fokus, setFokus] = useState(0);
  const [offenId, setOffenId] = useState<string | null>(null);
  const [antwort, setAntwort] = useState(false);
  const [markiert, setMarkiert] = useState<Set<string>>(new Set());
  const [beschaeftigt, setBeschaeftigt] = useState(false);
  const [suche, setSuche] = useState(params.get("q") ?? "");
  const [suchwort, setSuchwort] = useState(suche);
  const generation = useRef(0);
  const letzterKlick = useRef<number | null>(null);
  const freigabe = zaehler?.freigabe;

  const setParam = (k: string, v: string | null) => {
    const neu = new URLSearchParams(params);
    if (v) neu.set(k, v);
    else neu.delete(k);
    setParams(neu, { replace: true });
  };

  // Suchwort erst nach einer kurzen Pause übernehmen
  useEffect(() => {
    const t = window.setTimeout(() => setSuchwort(suche.trim()), 300);
    return () => window.clearTimeout(t);
  }, [suche]);

  useEffect(() => {
    setMarkiert(new Set());
    setOffenId(null);
    setAntwort(false);
    letzterKlick.current = null;
  }, [filter, wer, suchwort]);

  const laden = useCallback(
    async (still = false) => {
      const gen = ++generation.current;
      if (!still) setLaedt(true);
      setFehler(null);
      try {
        const r = await smsEingangApi.liste({ ...filterParameter(filter), user_id: wer, q: suchwort || undefined, limit: 100 });
        if (gen !== generation.current) return;
        setListe(r.data);
        setCursor(r.has_more ? r.cursor : null);
        setMarkiert((m) => {
          const da = new Set(r.data.map((x) => x.id));
          const rest = [...m].filter((id) => da.has(id));
          return rest.length === m.size ? m : new Set(rest);
        });
        if (!still) setFokus(0);
      } catch (e) {
        if (gen === generation.current) setFehler(e);
      } finally {
        if (gen === generation.current) setLaedt(false);
      }
    },
    [filter, wer, suchwort],
  );

  useEffect(() => {
    void laden();
  }, [laden]);

  const zeit = useRef<number | undefined>(undefined);
  useEchtzeit(["sms.received", "sms.updated"], () => {
    window.clearTimeout(zeit.current);
    zeit.current = window.setTimeout(() => {
      void laden(true);
      onZaehler();
    }, 400);
  });
  useNeuVerbunden(() => void laden(true));
  useEffect(() => () => window.clearTimeout(zeit.current), []);

  const mehr = async () => {
    if (!cursor) return;
    try {
      const r = await smsEingangApi.liste({ ...filterParameter(filter), user_id: wer, q: suchwort || undefined, limit: 100, cursor });
      setListe((l) => [...l, ...r.data.filter((x) => !l.some((y) => y.id === x.id))]);
      setCursor(r.has_more ? r.cursor : null);
    } catch (e) {
      setFehler(e);
    }
  };

  /** Neue Fassung einer Zeile übernehmen; passt sie nicht mehr zum Filter, fällt sie aus der Liste (außer sie ist aufgeklappt). */
  const uebernehmen = useCallback(
    (neu: Sms) => {
      setListe((l) => l.flatMap((x) => (x.id !== neu.id ? [x] : passtZumFilter(neu, filter) || neu.id === offenId ? [neu] : [])));
      setMarkiert((m) => {
        if (!m.has(neu.id)) return m;
        const rest = new Set(m);
        if (!passtZumFilter(neu, filter)) rest.delete(neu.id);
        return rest;
      });
    },
    [filter, offenId],
  );

  const leadOeffnen = (leadId: string) => {
    listeSetzen({
      quelle: "inbox", titel: "Inbox", pfad: `/inbox?${params.toString()}`, ids: [...new Set(liste.map((s) => s.lead_id).filter((x): x is string => !!x))],
      fokus: leadId,
    });
    navigate(`/lead/${encodeURIComponent(leadId)}`);
  };

  const gelesenSetzen = async (s: Sms, gelesen: boolean) => {
    if (s.richtung !== "eingehend" || s.gelesen === gelesen) return;
    uebernehmen({ ...s, gelesen });
    try {
      const r = await smsEingangApi.gelesen(s.id, gelesen);
      uebernehmen(r.sms);
      onZaehler();
    } catch (e) {
      uebernehmen(s);
      meldung.fehler(e, "Nicht gespeichert");
    }
  };

  const erledigtSetzen = async (s: Sms, erledigt = !s.erledigt) => {
    if (beschaeftigt || s.status === "geplant") return;
    setBeschaeftigt(true);
    try {
      const r = await smsEingangApi.erledigt(s.id, erledigt);
      uebernehmen(r.sms);
      onZaehler();
      if (r.aufgabe_fehler) meldung.zeige("info", `Die SMS ist erledigt, die Aufgabe in der Inbox nicht: ${r.aufgabe_fehler}`);
      else if (erledigt) meldung.ok("Erledigt.", { label: "Rückgängig", onClick: () => void erledigtSetzen({ ...r.sms }, false) });
    } catch (e) {
      meldung.fehler(e, "Nicht gespeichert");
    } finally {
      setBeschaeftigt(false);
    }
  };

  const abbrechen = async (s: Sms) => {
    setBeschaeftigt(true);
    try {
      await smsEingangApi.planAufheben(s.id);
      setListe((l) => l.filter((x) => x.id !== s.id));
      onZaehler();
      meldung.ok("Planung aufgehoben.");
    } catch (e) {
      meldung.fehler(e, "Nicht aufgehoben");
    } finally {
      setBeschaeftigt(false);
    }
  };

  const oeffnen = (s: Sms, i: number) => {
    setFokus(i);
    if (offenId === s.id) {
      setOffenId(null);
      setAntwort(false);
      return;
    }
    setOffenId(s.id);
    setAntwort(false);
    if (istUngelesen(s)) void gelesenSetzen(s, true);
  };

  // ---- Auswahlmodus -------------------------------------------------------------------------------------------
  const umschalten = (i: number, bereich: boolean) => {
    const neu = new Set(markiert);
    const an = !neu.has(liste[i].id);
    const gueltigVon = bereich && letzterKlick.current !== null && letzterKlick.current < liste.length;
    const von = gueltigVon ? Math.min(letzterKlick.current as number, i) : i;
    const bis = gueltigVon ? Math.max(letzterKlick.current as number, i) : i;
    for (let k = von; k <= bis; k++) (an ? neu.add(liste[k].id) : neu.delete(liste[k].id));
    letzterKlick.current = i;
    setMarkiert(neu);
  };
  const waehlbar = useMemo(() => liste.filter((s) => s.status !== "geplant"), [liste]);
  const alleAn = waehlbar.length > 0 && waehlbar.every((s) => markiert.has(s.id));
  const einigeAn = !alleAn && markiert.size > 0;

  const markierteErledigen = async () => {
    const ids = [...markiert];
    if (!ids.length || beschaeftigt) return;
    setBeschaeftigt(true);
    try {
      const r = await smsEingangApi.mehrereErledigt(ids, true);
      for (const e of r.ergebnisse) if (e.ok && e.sms) uebernehmen(e.sms);
      onZaehler();
      const schlecht = r.ergebnisse.filter((e) => !e.ok);
      if (r.ok) meldung.ok(`${r.ok} ${r.ok === 1 ? "Eintrag" : "Einträge"} erledigt.`);
      if (schlecht.length) {
        meldung.zeige("fehler", `${schlecht.length} nicht gespeichert: ${schlecht[0].fehler?.message ?? "Fehler"}`);
        setMarkiert(new Set(schlecht.map((e) => e.id)));
      } else setMarkiert(new Set());
    } catch (e) {
      meldung.fehler(e, "Nicht gespeichert");
    } finally {
      setBeschaeftigt(false);
    }
  };

  const markierteGelesen = async () => {
    const ids = [...markiert].filter((id) => liste.find((s) => s.id === id && istUngelesen(s)));
    if (!ids.length) return;
    try {
      await smsEingangApi.alleGelesen(ids);
      setListe((l) => l.map((s) => (ids.includes(s.id) ? { ...s, gelesen: true } : s)));
      onZaehler();
      setMarkiert(new Set());
    } catch (e) {
      meldung.fehler(e, "Nicht gespeichert");
    }
  };

  const alleGelesen = async () => {
    try {
      const r = await smsEingangApi.alleGelesen();
      setListe((l) => l.map((s) => (s.richtung === "eingehend" ? { ...s, gelesen: true } : s)));
      onZaehler();
      meldung.ok(r.anzahl ? `${r.anzahl} ${r.anzahl === 1 ? "SMS" : "SMS"} als gelesen markiert.` : "Alles war schon gelesen.");
    } catch (e) {
      meldung.fehler(e, "Nicht gespeichert");
    }
  };

  const aktuelle = liste[fokus];
  useTasten({
    j: () => setFokus((f) => Math.min(f + 1, liste.length - 1)),
    ArrowDown: () => setFokus((f) => Math.min(f + 1, liste.length - 1)),
    k: () => setFokus((f) => Math.max(f - 1, 0)),
    ArrowUp: () => setFokus((f) => Math.max(f - 1, 0)),
    Enter: () => aktuelle && oeffnen(aktuelle, fokus),
    "mod+Enter": () => aktuelle?.lead_id && window.open(`/lead/${encodeURIComponent(aktuelle.lead_id)}`, "_blank", "noopener"),
    x: () => aktuelle && aktuelle.status !== "geplant" && umschalten(fokus, false),
    e: () => (markiert.size ? void markierteErledigen() : aktuelle && void erledigtSetzen(aktuelle)),
    y: () => (markiert.size ? void markierteErledigen() : aktuelle && void erledigtSetzen(aktuelle)),
    u: () => aktuelle && void gelesenSetzen(aktuelle, !aktuelle.gelesen),
    r: () => {
      if (!aktuelle || aktuelle.richtung !== "eingehend" || !aktuelle.lead_id) return;
      if (offenId !== aktuelle.id) {
        setOffenId(aktuelle.id);
        if (istUngelesen(aktuelle)) void gelesenSetzen(aktuelle, true);
      }
      setAntwort(true);
    },
    Escape: () => {
      if (markiert.size) setMarkiert(new Set());
      else if (offenId) {
        setOffenId(null);
        setAntwort(false);
      }
    },
  });

  useEffect(() => {
    document.querySelector(`[data-sms="${liste[fokus]?.id}"]`)?.scrollIntoView({ block: "nearest" });
  }, [fokus, liste]);

  const zahl = (f: SmsFilter): number | null => {
    if (!zaehler) return null;
    if (f === "offen") return zaehler.offen ?? null;
    if (f === "ungelesen") return zaehler.ungelesen;
    if (f === "fehlgeschlagen") return zaehler.fehlgeschlagen;
    if (f === "geplant") return zaehler.geplant;
    return null;
  };

  const ungelesen = zaehler?.ungelesen ?? 0;
  return (
    <>
      <div className={`liste-leiste inbox-leiste sms-leiste${markiert.size ? " inbox-leiste--auswahl" : ""}`}>
        <input
          type="checkbox"
          className="inbox-leiste__alle"
          checked={alleAn}
          ref={(el) => {
            if (el) el.indeterminate = einigeAn;
          }}
          onChange={() => {
            letzterKlick.current = null;
            setMarkiert(alleAn ? new Set() : new Set(waehlbar.map((s) => s.id)));
          }}
          disabled={!waehlbar.length}
          title="Alle geladenen SMS markieren"
          aria-label="Alle geladenen SMS markieren"
        />
        {markiert.size ? (
          <>
            <span className="liste-leiste__zahl">{markiert.size} markiert</span>
            <button type="button" className="knopf knopf--klein" disabled={beschaeftigt} onClick={() => void markierteErledigen()} title="Markierte erledigen (e oder y)">
              <Icon name="haken" groesse={13} /> Erledigt
            </button>
            <button type="button" className="knopf knopf--klein" onClick={() => void markierteGelesen()} title="Markierte als gelesen markieren">
              Gelesen
            </button>
            <button type="button" className="knopf knopf--klein knopf--leise" onClick={() => setMarkiert(new Set())} title="Auswahl aufheben (Esc)">
              Auswahl aufheben
            </button>
          </>
        ) : (
          <>
            <div className="sms-filter" role="tablist" aria-label="Filter">
              {SMS_FILTER.map((f) => {
                const n = zahl(f.wert);
                return (
                  <button
                    key={f.wert}
                    type="button"
                    role="tab"
                    aria-selected={filter === f.wert}
                    title={f.titel}
                    className={`sms-filter__knopf${filter === f.wert ? " sms-filter__knopf--an" : ""}${f.wert === "fehlgeschlagen" && n ? " sms-filter__knopf--fehler" : ""}`}
                    onClick={() => setParam("filter", f.wert === "offen" ? null : f.wert)}
                  >
                    {f.label}
                    {n ? <span className="sms-filter__zahl">{n}</span> : null}
                  </button>
                );
              })}
            </div>
            <span className="liste-leiste__tasten leise sms-leiste__tasten">
              <kbd>j</kbd>/<kbd>k</kbd> · <kbd>Enter</kbd> öffnen · <kbd>r</kbd> antworten · <kbd>e</kbd> erledigt
            </span>
            <span className="sms-leiste__rechts">
              {ungelesen > 0 ? (
                <button type="button" className="knopf knopf--klein knopf--leise" onClick={() => void alleGelesen()} title="Alle eingegangenen SMS als gelesen markieren">
                  Alle gelesen
                </button>
              ) : null}
              <span className="sms-suche">
                <Icon name="suche" groesse={13} />
                <input
                  className="sms-suche__feld"
                  type="search"
                  value={suche}
                  placeholder="Suchen …"
                  aria-label="SMS durchsuchen (Text, Lead, Nummer)"
                  onChange={(e) => {
                    setSuche(e.target.value);
                    setParam("q", e.target.value.trim() || null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") (e.target as HTMLInputElement).blur();
                  }}
                />
              </span>
            </span>
          </>
        )}
      </div>
      {filter === "geplant" && freigabe === false ? (
        <div className="hinweis hinweis--warnung sms-hinweis" role="status">
          <Icon name="warnung" groesse={15} />
          <span>Der Versand geplanter SMS ist noch nicht eingeschaltet. Sie bleiben gespeichert und gehen erst raus, wenn er in den SMS-Einstellungen freigegeben ist.</span>
        </div>
      ) : null}
      {fehler ? <FehlerBox fehler={fehler} onNochmal={() => void laden()} /> : null}
      {laedt && !liste.length ? (
        <Laden />
      ) : !fehler && !liste.length ? (
        <Leer titel={suchwort ? "Keine Treffer." : LEER[filter].titel} text={suchwort ? "Zu dieser Suche gibt es keine SMS im Filter." : LEER[filter].text} />
      ) : (
        <div className="aufgaben-liste aufgaben-liste--sms">
          {liste.map((s, i) => (
            <div key={s.id}>
              <SmsZeile
                sms={s}
                fokus={i === fokus}
                offen={offenId === s.id}
                eigene={!s.user_id || s.user_id === nutzer?.id || s.user_id === nutzer?.close_user_id}
                an={markiert.has(s.id)}
                beschaeftigt={beschaeftigt}
                onUmschalten={(bereich) => (setFokus(i), umschalten(i, bereich))}
                onOeffnen={() => oeffnen(s, i)}
                onErledigt={() => void erledigtSetzen(s)}
                onLead={() => s.lead_id && leadOeffnen(s.lead_id)}
                onGelesen={() => void gelesenSetzen(s, !s.gelesen)}
                onAbbrechen={() => void abbrechen(s)}
              />
              {offenId === s.id ? (
                <SmsVerlauf
                  sms={s}
                  antwortOffen={antwort}
                  onAntwort={setAntwort}
                  onLead={leadOeffnen}
                  onGesendet={() => {
                    onZaehler();
                    if (!s.erledigt && s.richtung === "eingehend") void erledigtSetzen(s, true);
                  }}
                />
              ) : null}
            </div>
          ))}
          {cursor ? (
            <div className="tabelle__mehr">
              <button type="button" className="knopf" onClick={() => void mehr()}>
                Weitere laden
              </button>
            </div>
          ) : null}
        </div>
      )}
    </>
  );
}
